import { useCallback, useReducer } from 'react'
import { isNil } from 'ramda'
import jwtDecode from 'jwt-decode'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useMutation } from '@tanstack/react-query'

import effects from 'app/effects/effects'
import { generatePKCE } from 'commons/pkce'
import { useAppName } from 'commons/usePartner'
import { errorCode } from 'app/constants/errorCode'
import * as authActions from 'app/auth/store/actions'
import { socialLoginScop, socialLoginFields } from 'app/constants/login'

import MessageAlert from 'app/components/MessageAlert'

import { step, action } from './constants'

export const ActionType = {
  SET_REF: 'SET_REF',
  SET_CODE: 'SET_CODE',
  SET_STEP: 'SET_STEP',
  SET_EMAIL: 'SET_EMAIL',
  SET_SUSPEND: 'SET_SUSPEND',
  SET_ERROR_OTP: 'SET_ERROR_OTP',
  SET_OTP_ACTION: 'SET_OTP_ACTION',
  SET_LOADER_PAGE: 'SET_LOADER_PAGE',
  SET_NEW_PASSWORD: 'SET_NEW_PASSWORD',
  SET_PROVIDER_DATA: 'SET_PROVIDER_DATA',
}

const initialState = {
  ref: null,
  code: null,
  email: null,
  reason: null,
  action: null,
  prevPage: null,
  isErrorOtp: false,
  newPassword: null,
  page: step.isLogin,
  isLoaderPage: true,
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_STEP:
      return {
        ...state,
        prevPage: state.page,
        page: action.page,
      }
    case ActionType.SET_EMAIL:
      return {
        ...state,
        email: action.value,
      }
    case ActionType.SET_REF:
      return {
        ...state,
        ref: action.value,
        code: action.code,
      }
    case ActionType.SET_SUSPEND:
      return {
        ...state,
        page: action.page,
        reason: action.reason,
      }
    case ActionType.SET_ERROR_OTP:
      return {
        ...state,
        isErrorOtp: action.value,
      }
    case ActionType.SET_OTP_ACTION:
      return {
        ...state,
        action: action.action,
        isErrorOtp: false,
      }
    case ActionType.SET_NEW_PASSWORD:
      return {
        ...state,
        newPassword: action.value,
      }
    case ActionType.SET_PROVIDER_DATA:
      return {
        ...state,
        ...action.data,
      }
    case ActionType.SET_LOADER_PAGE:
      return {
        ...state,
        isLoaderPage: action.value,
      }
    case ActionType.SET_CODE:
      return {
        ...state,
        code: action.value,
      }
    default:
      return state
  }
}

export default function useLogin() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const appName = useAppName()
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()

  const errors = useCallback(
    (text) => {
      const generalError = formatMessage({ id: 'error.InternalServerError' })
      return commonDispatch(
        MessageAlert.showError({
          message: text ?? generalError,
        }),
      )
    },
    [commonDispatch, formatMessage],
  )

  const handleAlertError = useCallback(
    ({ name, reason }) => {
      if (name === errorCode.PlaceSuspended) {
        dispatch({
          type: 'SET_SUSPEND',
          page: step.isSuspendedAccount,
          reason: reason,
        })
        return
      } else {
        errors(
          formatMessage(
            {
              id: `error.${name}`,
            },
            { newline: <br />, appName },
          ),
        )
      }
    },
    [appName, errors, formatMessage],
  )

  const handlePage = useCallback((valueKey) => {
    dispatch({
      type: 'SET_STEP',
      page: valueKey,
    })
  }, [])

  const handleLoaderPage = useCallback((value) => {
    dispatch({
      type: 'SET_LOADER_PAGE',
      value: value,
    })
  }, [])

  const handleOTPVerifyState = useCallback(
    (token) => {
      if (state.action === action.signIn) {
        handleLoaderPage(true)
        commonDispatch(authActions.login(token))
      } else if (state.action === action.signUp) {
        handlePage(step.isNewAccount)
      }
    },
    [commonDispatch, handleLoaderPage, handlePage, state.action],
  )

  const fetchOTP = useMutation({
    mutationFn: async (action) => {
      dispatch({
        type: 'SET_OTP_ACTION',
        action: action,
      })
      const response = await effects.apis.authV2.otpRequest({
        email: state.email,
        action: action,
      })
      return response
    },
    onSuccess: ({ ref, code }) => {
      dispatch({
        type: 'SET_REF',
        value: ref,
        code: code,
      })
    },
    onError: (error) => {
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  const handleLogin = useMutation({
    mutationFn: async (value) => {
      dispatch({
        type: 'SET_EMAIL',
        value: value.email,
      })
      const response = await effects.apis.authV2.verifyEmail({
        email: value.email.trim(),
        reCaptcha: value.token,
      })
      return response
    },
    onSuccess: async ({ user }) => {
      let page
      if (user && user.hasPassword) page = step.isVerifyApproach
      else if (user && !user.hasPassword) {
        await fetchOTP.mutate(action.signIn)
        page = step.isOTP
      } else {
        await fetchOTP.mutate(action.signUp)
        page = step.isOTP
      }

      handlePage(page)
    },
    onError: (error) => {
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  const verifyPassword = useMutation({
    mutationFn: async ({ password }) => {
      const response = await effects.apis.authV2.authVerify({
        email: state.email,
        password: password,
        action: action.password,
      })
      return response
    },
    onSuccess: ({ token, verify }) => {
      if (verify) commonDispatch(authActions.login(token))
      else {
        errors(
          formatMessage({
            id: 'login.error.401',
          }),
        )
      }
    },
    onError: (error) => {
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  const verifyOTP = useMutation({
    mutationFn: async (otp) => {
      dispatch({
        type: 'SET_ERROR_OTP',
        value: false,
      })
      const response = await effects.apis.authV2.authVerify({
        email: state.email,
        action: state.action,
        ref: state.ref,
        otp: otp,
      })
      return response
    },
    onSuccess: ({ token, verify }) => {
      if (verify) {
        handleOTPVerifyState(token)
      } else {
        errors(formatMessage({ id: 'teamMembers.otp.incorrect' }))
        dispatch({
          type: 'SET_ERROR_OTP',
          value: true,
        })
      }
    },
    onError: (error) => {
      handlePage(step.isOTP)
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  const handleVerifyApproach = async (valueKey) => {
    if (valueKey === step.isOTP) {
      await fetchOTP.mutate(action.signIn)
    }
    handlePage(valueKey)
  }

  const handleNewPassword = async ({ password }) => {
    dispatch({
      type: 'SET_NEW_PASSWORD',
      value: password,
    })
    handlePage(step.isNewAccount)
    return commonDispatch(
      MessageAlert.showSuccess({
        message:
          'Your MessageSpring account password has been set up successfully.',
      }),
    )
  }

  const createNewAccount = useMutation({
    mutationFn: async (value) => {
      handleLoaderPage(true)
      const response = await effects.apis.authV2.registration({
        code: value.code,
        email: value.email,
        firstName: value.firstName,
        lastName: value.lastName,
        password: state.newPassword,
      })
      return response
    },
    onSuccess: ({ token }) => {
      commonDispatch(authActions.login(token))
    },
    onError: (error) => {
      handleLoaderPage(false)
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  const verifyProvider = useMutation({
    mutationFn: async (value) => {
      const response = await effects.apis.authV2.provider(value)
      return response
    },
    onSuccess: ({ token, code, verify }, value) => {
      if (verify) {
        commonDispatch(authActions.login(token))
      } else {
        //keep data for Provider
        dispatch({
          type: 'SET_CODE',
          value: code,
        })
        localStorage.setItem(
          'newAccountData',
          JSON.stringify({
            email: value?.email,
            firstName: value?.firstName ?? '',
            lastName: value?.lastName ?? '',
          }),
        )
        handlePage(step.isNewAccount)
      }
    },
    onError: (error) => {
      handleLoaderPage(false)
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  const handleProvider = useCallback(
    async (value) => {
      if (isNil(value.email)) {
        handlePage(step.isPermissionRequired)
        return
      } else {
        handleLoaderPage(true)
        dispatch({
          type: 'SET_PROVIDER_DATA',
          value,
        })
        await verifyProvider.mutate(value)
      }
    },
    [handleLoaderPage, handlePage, verifyProvider],
  )

  const handleFacebook = useCallback(
    async (code, verifier) => {
      try {
        const res = await fetch(
          `https://graph.facebook.com/v11.0/oauth/access_token?` +
            new URLSearchParams({
              client_id: process.env.REACT_APP_FACEBOOK_LOGIN_KEY,
              redirect_uri: `${process.env.REACT_APP_REDIRECT_URI}auth/facebook/callback`,
              code,
              code_verifier: verifier,
            }),
          { method: 'GET' },
        )
        const data = await res.json()
        const decode = jwtDecode(data.id_token)
        handleProvider({
          provider: 'facebook',
          sub: decode?.sub,
          firstName: decode?.given_name,
          lastName: decode?.family_name,
          email: decode?.email,
          profiltPicture: decode?.picture ?? null,
          token: data.id_token,
        })
      } catch {
        errors()
      }
    },
    [errors, handleProvider],
  )

  const handleFacebookAuth = useCallback(async () => {
    const { verifier, challenge } = await generatePKCE()
    sessionStorage.setItem('fb_pkce_verifier', verifier)

    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_FACEBOOK_LOGIN_KEY,
      redirect_uri: `${process.env.REACT_APP_REDIRECT_URI}auth/facebook/callback`,
      response_type: 'code',
      scope: socialLoginScop.facebook,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state: Math.random().toString(36).substring(2),
      nonce: Math.random().toString(36).substring(2),
      fields: socialLoginFields.facebook,
    })
    window.location.href = `https://www.facebook.com/v11.0/dialog/oauth?${params.toString()}`
  }, [])

  const handleGoole = useCallback(async (value) => {
    let result
    if (value.access_token) {
      try {
        const profileRes = await fetch(
          'https://openidconnect.googleapis.com/v1/userinfo',
          {
            method: 'GET',
            headers: new Headers({
              Authorization: `Bearer ${value.access_token}`,
            }),
          },
        )
        if (!profileRes.ok) {
          errors()
        }
        const profileData = await profileRes.json()
        result = {
          provider: 'google',
          sub: profileData?.sub,
          firstName: profileData?.given_name,
          lastName: profileData?.family_name,
          email: profileData?.email,
          profiltPicture: profileData?.picture ?? '',
          token: value.access_token,
        }
      } catch {
        errors()
      }
    }

    // One-Tap have credential
    if (value.credential) {
      const decode = jwtDecode(value.credential)
      result = {
        provider: 'google',
        sub: decode?.sub,
        firstName: decode?.given_name,
        lastName: decode?.family_name,
        email: decode?.email,
        profiltPicture: decode?.picture ?? '',
        token: value.credential,
      }
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    state,
    fetchOTP,
    verifyOTP,
    handlePage,
    handleLogin,
    handleGoole,
    handleProvider,
    handleFacebook,
    verifyProvider,
    verifyPassword,
    createNewAccount,
    handleLoaderPage,
    handleNewPassword,
    handleFacebookAuth,
    handleVerifyApproach,
  }
}
